/**
 * DashboardCharts.tsx
 *
 * Analytics section for the Dashboard page.
 *
 * Charts rendered:
 *  1. Status Distribution – donut chart (Expired / High Risk / Upcoming / Active / Missing)
 *  2. Expiry Timeline     – bar chart (documents expiring per month, ±6 months)
 *  3. Category Breakdown  – horizontal bar chart (top categories by count)
 *  4. Documents Over Time – area chart (cumulative additions by month based on created_at)
 */

import React, { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";
import {
  differenceInDays,
  parseISO,
  format,
  startOfDay,
  addMonths,
  startOfMonth,
  endOfMonth,
  isWithinInterval,
} from "date-fns";
import type { ExpiryItem } from "../hooks/useExpiryItems";
import { HIGH_RISK_DAYS, UPCOMING_DAYS } from "../constants/expiry";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getStatus(item: ExpiryItem) {
  if (!item.expiry_date) return "missing";
  const today = startOfDay(new Date());
  const expiry = startOfDay(parseISO(item.expiry_date));
  const days = differenceInDays(expiry, today);
  if (days < 0) return "expired";
  if (days <= HIGH_RISK_DAYS) return "high-risk";
  if (days <= UPCOMING_DAYS) return "upcoming";
  return "active";
}

// ─── Shared custom tooltip ─────────────────────────────────────────────────

interface TooltipPayloadEntry {
  name: string;
  value: number;
  color?: string;
  fill?: string;
}

const ChartTooltip: React.FC<{
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
  unit?: string;
}> = ({ active, payload, label, unit = "documents" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg shadow-lg px-3 py-2 text-xs">
      {label && <p className="font-semibold text-[#111827] mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }} className="font-medium">
          {p.name}: <span className="font-bold">{p.value}</span> {unit}
        </p>
      ))}
    </div>
  );
};

// ─── Donut centre label ────────────────────────────────────────────────────

const DonutLabel: React.FC<{ total: number }> = ({ total }) => (
  <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle">
    <tspan x="50%" dy="-6" fontSize="22" fontWeight="700" fill="#111827">
      {total}
    </tspan>
    <tspan x="50%" dy="18" fontSize="11" fill="#6B7280">
      total
    </tspan>
  </text>
);

// ─── Props ────────────────────────────────────────────────────────────────────

interface DashboardChartsProps {
  items: ExpiryItem[];
}

// ─── Component ────────────────────────────────────────────────────────────────

const DashboardCharts: React.FC<DashboardChartsProps> = ({ items }) => {
  // 1. Status distribution ─────────────────────────────────────────────────
  const statusData = useMemo(() => {
    const counts = {
      expired: 0,
      "high-risk": 0,
      upcoming: 0,
      active: 0,
      missing: 0,
    };
    items.forEach((item) => {
      counts[getStatus(item)]++;
    });
    return [
      { name: "Expired", value: counts.expired, color: "#EF4444" },
      { name: "High Risk", value: counts["high-risk"], color: "#F97316" },
      { name: "Upcoming", value: counts.upcoming, color: "#EAB308" },
      { name: "Active", value: counts.active, color: "#22C55E" },
      { name: "Missing", value: counts.missing, color: "#94A3B8" },
    ].filter((d) => d.value > 0);
  }, [items]);

  // 2. Expiry timeline (current month -1 to +5) ─────────────────────────────
  const timelineData = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const month = addMonths(startOfMonth(today), i - 1);
      const monthEnd = endOfMonth(month);
      const count = items.filter((item) => {
        if (!item.expiry_date) return false;
        const d = parseISO(item.expiry_date);
        return isWithinInterval(d, { start: month, end: monthEnd });
      }).length;
      return {
        month: format(month, "MMM yy"),
        count,
        isCurrent: i === 1, // index 1 = current month
      };
    });
  }, [items]);

  // 3. Category breakdown (top 8) ───────────────────────────────────────────
  const categoryData = useMemo(() => {
    const map: Record<string, number> = {};
    items.forEach((item) => {
      const cat = item.category || "Uncategorised";
      map[cat] = (map[cat] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, count]) => ({ name, count }));
  }, [items]);

  // 4. Additions over time (last 6 months cumulative) ───────────────────────
  const additionData = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 6 }, (_, i) => {
      const month = addMonths(startOfMonth(today), i - 5);
      const monthEnd = endOfMonth(month);
      const added = items.filter((item) => {
        if (!item.created_at) return false;
        const d = parseISO(item.created_at);
        return isWithinInterval(d, { start: month, end: monthEnd });
      }).length;
      return { month: format(month, "MMM yy"), added };
    });
  }, [items]);

  // Accumulate additions for running total
  const cumulativeData = useMemo(() => {
    let total = 0;
    return additionData.map((d) => {
      total += d.added;
      return { ...d, total };
    });
  }, [additionData]);

  // ─── KPI quick-stat ──────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const today = startOfDay(new Date());
    const expiredCount = items.filter(
      (i) =>
        i.expiry_date &&
        differenceInDays(startOfDay(parseISO(i.expiry_date)), today) < 0
    ).length;
    const highRiskCount = items.filter((i) => {
      const s = getStatus(i);
      return s === "high-risk";
    }).length;
    const activeCount = items.filter((i) => getStatus(i) === "active").length;
    const missingCount = items.filter((i) => !i.expiry_date).length;
    return { expiredCount, highRiskCount, activeCount, missingCount };
  }, [items]);

  if (items.length === 0) return null;

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5 mb-6">
      {/* ── KPI row ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          {
            label: "Total Documents",
            value: items.length,
            bg: "bg-[#EFF6FF]",
            text: "text-[#2563EB]",
          },
          {
            label: "Active",
            value: kpis.activeCount,
            bg: "bg-[#F0FDF4]",
            text: "text-[#15803D]",
          },
          {
            label: "High Risk",
            value: kpis.highRiskCount,
            bg: "bg-[#FFF7ED]",
            text: "text-[#EA580C]",
          },
          {
            label: "Expired",
            value: kpis.expiredCount,
            bg: "bg-[#FEF2F2]",
            text: "text-[#DC2626]",
          },
        ].map((kpi) => (
          <div
            key={kpi.label}
            className={`${kpi.bg} rounded-xl px-4 py-3 flex flex-col gap-0.5`}
          >
            <span className={`text-2xl font-bold ${kpi.text}`}>
              {kpi.value}
            </span>
            <span className="text-xs text-[#6B7280] font-medium">
              {kpi.label}
            </span>
          </div>
        ))}
      </div>

      {/* ── Row 1: Status Donut + Expiry Timeline ────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Status Distribution */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-5">
          <h3 className="text-sm font-semibold text-[#111827] mb-4">
            Status Distribution
          </h3>
          <div className="flex items-center gap-4">
            <div style={{ width: 160, height: 160, flexShrink: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={72}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                  <DonutLabel total={items.length} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <ul className="flex-1 space-y-2">
              {statusData.map((d) => (
                <li
                  key={d.name}
                  className="flex items-center justify-between text-xs"
                >
                  <span className="flex items-center gap-2 text-[#374151]">
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0"
                      style={{ background: d.color }}
                    />
                    {d.name}
                  </span>
                  <span className="font-semibold text-[#111827]">
                    {d.value}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Expiry Timeline */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-5">
          <h3 className="text-sm font-semibold text-[#111827] mb-4">
            Expiry Timeline
          </h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart
              data={timelineData}
              margin={{ top: 0, right: 0, left: -20, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#F3F4F6"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "#9CA3AF" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: "#9CA3AF" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip unit="expiring" />} />
              <Bar dataKey="count" name="Documents" radius={[4, 4, 0, 0]}>
                {timelineData.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={entry.isCurrent ? "#2563EB" : "#BFDBFE"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="text-xs text-[#9CA3AF] mt-2">
            <span className="inline-block h-2 w-2 rounded-sm bg-[#2563EB] mr-1" />
            Current month&nbsp;&nbsp;
            <span className="inline-block h-2 w-2 rounded-sm bg-[#BFDBFE] mr-1" />
            Other months
          </p>
        </div>
      </div>

      {/* ── Row 2: Category Breakdown + Additions Over Time ──────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Category Breakdown */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-5">
          <h3 className="text-sm font-semibold text-[#111827] mb-4">
            Category Breakdown
          </h3>
          <ResponsiveContainer
            width="100%"
            height={Math.max(120, categoryData.length * 30)}
          >
            <BarChart
              layout="vertical"
              data={categoryData}
              margin={{ top: 0, right: 16, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#F3F4F6"
                horizontal={false}
              />
              <XAxis
                type="number"
                allowDecimals={false}
                tick={{ fontSize: 11, fill: "#9CA3AF" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={110}
                tick={{ fontSize: 11, fill: "#374151" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} />
              <Bar
                dataKey="count"
                name="Documents"
                fill="#7C3AED"
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Documents Added Over Time */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-5">
          <h3 className="text-sm font-semibold text-[#111827] mb-1">
            Documents Added
          </h3>
          <p className="text-xs text-[#9CA3AF] mb-4">
            Running total over the last 6 months
          </p>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart
              data={cumulativeData}
              margin={{ top: 0, right: 0, left: -20, bottom: 0 }}
            >
              <defs>
                <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563EB" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#F3F4F6"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "#9CA3AF" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: "#9CA3AF" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip unit="total" />} />
              <Area
                type="monotone"
                dataKey="total"
                name="Total"
                stroke="#2563EB"
                strokeWidth={2}
                fill="url(#totalGrad)"
                dot={{ fill: "#2563EB", r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default DashboardCharts;
